import { createElement, useMemo, useState } from 'react';

import type { SelectOption } from '@/lib/databases/values';

import { SelectChip } from './SelectChip.web';

function selectedIds(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map(String).filter(Boolean);
  }
  if (value === undefined || value === null || value === '') {
    return [];
  }
  return [String(value)];
}

export function SelectFilterValuePicker({
  options,
  value,
  disabled,
  onChange,
}: {
  options: SelectOption[];
  value: unknown;
  disabled?: boolean;
  onChange: (ids: string[]) => void;
}) {
  const [query, setQuery] = useState('');
  const ids = useMemo(() => selectedIds(value), [value]);
  const idSet = useMemo(() => new Set(ids), [ids]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return options;
    }
    return options.filter((o) => o.name.toLowerCase().includes(q));
  }, [options, query]);

  function toggle(id: string) {
    const next = idSet.has(id) ? ids.filter((x) => x !== id) : [...ids, id];
    onChange(next);
  }

  return createElement(
    'div',
    { className: 'cs-db-filter-select-value' },
    createElement('input', {
      type: 'search',
      className: 'cs-db-filter-select-search',
      placeholder: 'Select one or more options…',
      value: query,
      disabled,
      onChange: (e: Event) => setQuery((e.target as HTMLInputElement).value),
      onMouseDown: (e: MouseEvent) => e.stopPropagation(),
      onClick: (e: MouseEvent) => e.stopPropagation(),
    }),
    ids.length > 0
      ? createElement(
          'div',
          { className: 'cs-db-filter-select-chips' },
          ...ids.map((id) => {
            const opt = options.find((o) => o.id === id);
            if (!opt) {
              return null;
            }
            return createElement(SelectChip, {
              key: id,
              label: opt.name,
              color: opt.color,
              onRemove: disabled ? undefined : () => toggle(id),
            });
          }),
        )
      : null,
    createElement(
      'div',
      { className: 'cs-db-filter-select-options', role: 'listbox' },
      filtered.length === 0
        ? createElement('p', { className: 'cs-db-filter-select-empty' }, 'No options')
        : filtered.map((opt) =>
            createElement(
              'label',
              {
                key: opt.id,
                className: 'cs-db-filter-select-option',
                onMouseDown: (e: MouseEvent) => e.stopPropagation(),
              },
              createElement('input', {
                type: 'checkbox',
                checked: idSet.has(opt.id),
                disabled,
                onChange: () => toggle(opt.id),
                onMouseDown: (e: MouseEvent) => e.stopPropagation(),
              }),
              createElement(SelectChip, { label: opt.name, color: opt.color }),
            ),
          ),
    ),
    createElement(
      'button',
      {
        type: 'button',
        className: 'cs-db-filter-select-clear',
        disabled: disabled || ids.length === 0,
        onClick: (e: MouseEvent) => {
          e.stopPropagation();
          onChange([]);
        },
        onMouseDown: (e: MouseEvent) => e.stopPropagation(),
      },
      'Clear',
    ),
  );
}
